/**
 * ChipMultiSelect
 *
 * A chip-based multi-select dropdown with:
 * - Selected values rendered as deletable chips in the input
 * - Checkbox menu items with optional icons
 * - "Only" button on hover to quick-select a single value
 * - Clear button to reset all selections
 * - Controlled open state for programmatic menu close
 */

import React, { useCallback, useState } from 'react';
import { Clear as ClearIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from '@mui/material';

export interface ChipMultiSelectProps {
  /** HTML id for the InputLabel (used by Select's labelId) */
  labelId: string;
  /** Label text displayed in the input */
  label: string;
  /** Currently selected values */
  value: string[];
  /** Available options to select from */
  options: ReadonlyArray<{ value: string; label: string }>;
  /** Called when selection changes */
  onChange: (values: string[]) => void;
  /** Called when "Only" is clicked on an option */
  onOnly: (value: string) => void;
  /** Called when the clear button is clicked */
  onClear: () => void;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Minimum width of the FormControl. @default 140 */
  minWidth?: number;
  /** Optional icon map keyed by option value */
  icons?: Record<string, React.ElementType>;
}

const ONLY_BUTTON_SX = {
  fontSize: '0.65rem',
  py: 0,
  px: 1,
  minWidth: 'auto',
  textTransform: 'none',
} as const;

const MENU_ITEM_SX = {
  justifyContent: 'space-between',
  gap: 2,
  '& .only-btn': { opacity: 0, transition: 'opacity 150ms' },
  '&:hover .only-btn': { opacity: 1 },
} as const;

export function ChipMultiSelect({
  labelId,
  label,
  value,
  options,
  onChange,
  onOnly,
  onClear,
  disabled,
  minWidth = 140,
  icons,
}: ChipMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const hasValue = value.length > 0;

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const val = event.target.value;
      onChange(typeof val === 'string' ? val.split(',') : val);
    },
    [onChange],
  );

  const handleToggle = useCallback(
    (item: string) => {
      const idx = value.indexOf(item);
      const next = [...value];
      if (idx === -1) {
        next.push(item);
      } else {
        next.splice(idx, 1);
      }
      onChange(next);
    },
    [value, onChange],
  );

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear();
      (document.activeElement as HTMLElement)?.blur();
    },
    [onClear],
  );

  const handleOnlyClick = useCallback(
    (e: React.MouseEvent, item: string) => {
      e.stopPropagation();
      onOnly(item);
      setOpen(false);
    },
    [onOnly],
  );

  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel id={labelId} shrink={hasValue}>{label}</InputLabel>
      <Select
        labelId={labelId}
        multiple
        displayEmpty
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        input={
          <OutlinedInput
            label={hasValue ? label : undefined}
            notched={hasValue}
            endAdornment={
              hasValue ? (
                <IconButton size="small" onClick={handleClearClick} sx={{ mr: 1.5 }}>
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : null
            }
          />
        }
        renderValue={selected =>
          selected.length === 0 ? (
            <></>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map(v => {
                const opt = options.find(o => o.value === v);
                return (
                  <Chip
                    key={v}
                    label={opt?.label ?? v}
                    size="small"
                    onDelete={(e: React.SyntheticEvent) => {
                      e.stopPropagation();
                      handleToggle(v);
                      (document.activeElement as HTMLElement)?.blur();
                    }}
                    onMouseDown={(e: React.SyntheticEvent) => e.stopPropagation()}
                  />
                );
              })}
            </Box>
          )
        }
      >
        {options.map(opt => {
          const Icon = icons?.[opt.value];
          return (
            <MenuItem key={opt.value} value={opt.value} sx={MENU_ITEM_SX}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox size="small" checked={value.includes(opt.value)} />
                {Icon && (
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                )}
                <ListItemText primary={opt.label} />
              </Box>
              <Button
                className="only-btn"
                size="small"
                variant="outlined"
                onClick={e => handleOnlyClick(e, opt.value)}
                sx={ONLY_BUTTON_SX}
              >
                Only
              </Button>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

ChipMultiSelect.displayName = 'ChipMultiSelect';
